import * as React from "react"

import { cva } from "./cva.ts"

export const Marquee = React.forwardRef<HTMLElement, MarqueeProps>(
  ({ children, className, ...props }, ref) => {
    const marqueeRef = React.useRef<HTMLElement | null>(null)
    const animations = React.useRef<Animation[]>([]).current

    React.useImperativeHandle(ref, () => marqueeRef.current!, [])

    React.useEffect(getEffectForMarquee(marqueeRef, animations), [])

    return (
      <span className={classForMarque({ className })} ref={marqueeRef} {...props}>
        <span className={classForMarqueeContent()}>{children}</span>
        <span className={classForMarqueeContent()} />
      </span>
    )
  },
)

export const classForMarque = cva(
  "flex gap-[0_var(--gap)] justify-center overflow-hidden select-none [--gap:80px]",
)

const classForMarqueeContent = cva("flex shrink-0 gap-[0_var(--gap)] will-change-transform")

export type MarqueeProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>

// #region Internals

const debounce = <T extends (...args: any[]) => any>(callback: T, delay: number) => {
  let timeout: ReturnType<typeof setTimeout>

  return (...args: Parameters<T>) => {
    clearTimeout(timeout)

    timeout = setTimeout(() => callback(...args), delay)
  }
}

const getEffectForMarquee =
  (marqueeRef: React.MutableRefObject<HTMLElement | null>, animations: Animation[]) => () => {
    const marquee = marqueeRef.current

    if (marquee) {
      const marqueeAnimator = getMarqueeAnimator(marquee, animations)

      const observer = new ResizeObserver(debounce(marqueeAnimator, 200))

      observer.observe(marquee)

      marqueeAnimator()

      return () => {
        observer.disconnect()
      }
    }
  }

const getMarqueeAnimator = (marquee: HTMLElement, animations: Animation[]) => {
  let [content, cloned] = [...marquee.children] as HTMLElement[]
  let elements = [...content.children] as HTMLElement[]

  for (const animation of animations.splice(0)) {
    animation.cancel()
  }

  return (arg?: any) => {
    const needsSpace = marquee.clientWidth > content.clientWidth

    if (!arg || needsSpace) {
      if (needsSpace) {
        content.replaceChildren(...elements)
      }

      cloned.replaceChildren()

      while (marquee.clientWidth > content.clientWidth) {
        content.append(...elements.map((node) => node.cloneNode(true)))
      }

      cloned.replaceChildren(...content.cloneNode(true).childNodes)

      const duration = 20 * content.clientWidth

      animations.push(
        content.animate([{ translate: "50% 0%" }, { translate: "calc(-50% - var(--gap,0)) 0%" }], {
          duration,
          easing: "linear",
          id: "scroll",
          iterations: Infinity,
        }),
        cloned.animate([{ translate: "50% 0%" }, { translate: "calc(-50% - var(--gap, 0)) 0%" }], {
          duration,
          easing: "linear",
          id: "scroll",
          iterations: Infinity,
        }),
      )
    }
  }
}

// #endregion
